import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Star, MapPin, ArrowDown, Phone } from "lucide-react";
import { BUSINESS, IMAGES } from "@/constants";
import { MaskedLine } from "@/components/Reveal";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative h-[100svh] min-h-[640px] overflow-hidden flex items-end"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <motion.img
          src={IMAGES.hero}
          alt="Shine Space Beauty Salon interior"
          initial={{ scale: 1.15, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 2, ease: [0.16, 1, 0.3, 1] }}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/10" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/60 via-transparent to-transparent" />
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 w-full max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pb-24 sm:pb-28"
      >
        <MaskedLine delay={0.5}>
          <div className="flex items-center gap-4 mb-8">
            <span className="h-px w-12 bg-gold" />
            <span className="text-xs font-semibold tracking-[0.3em] uppercase text-gold">
              Ladies Salon · Bur Dubai
            </span>
          </div>
        </MaskedLine>

        <h1 className="font-serif font-light leading-[0.95] tracking-tight">
          <MaskedLine delay={0.7}>
            <span className="block text-5xl sm:text-7xl lg:text-8xl text-bone">
              Where Bur Dubai
            </span>
          </MaskedLine>
          <MaskedLine delay={0.85}>
            <span className="block text-5xl sm:text-7xl lg:text-8xl">
              comes to <em className="text-gold-gradient not-italic font-normal italic">shine.</em>
            </span>
          </MaskedLine>
        </h1>

        <MaskedLine delay={1.05}>
          <p className="mt-8 max-w-xl text-base sm:text-lg text-sand leading-relaxed">
            Hair, nails, facials, waxing & henna — a private ladies-only
            sanctuary on Khalid Bin Al Waleed Road. Walk in glowing,
            walk out radiant.
          </p>
        </MaskedLine>

        <MaskedLine delay={1.2}>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href="#book"
              data-testid="hero-book-button"
              className="group relative overflow-hidden text-sm font-semibold tracking-widest uppercase bg-gold text-ink px-8 py-4 rounded-full hover:bg-goldlight transition-colors duration-300"
            >
              Book Appointment
            </a>
            <a
              href={BUSINESS.whatsappWithText(
                "Hi Shine Space! I'd like to book an appointment."
              )}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-button"
              className="text-sm font-semibold tracking-widest uppercase text-bone border border-bone/25 px-8 py-4 rounded-full hover:border-gold hover:text-gold transition-colors duration-300"
            >
              WhatsApp Us
            </a>
            <div
              data-testid="hero-rating"
              className="flex items-center gap-2 text-sm text-sand ml-1"
            >
              <Star size={15} className="fill-gold text-gold" />
              <span className="text-bone font-semibold">{BUSINESS.rating}</span>
              <span>· {BUSINESS.reviewCount} Google reviews</span>
            </div>
          </div>
        </MaskedLine>

        <MaskedLine delay={1.35}>
          <div className="mt-12 flex items-center gap-2 text-xs tracking-widest uppercase text-sand/70">
            <MapPin size={13} className="text-gold" />
            {BUSINESS.address}
          </div>
        </MaskedLine>
      </motion.div>

      <motion.a
        href="#services"
        data-testid="hero-scroll-indicator"
        style={{ opacity: fade }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute z-10 bottom-8 right-8 hidden sm:flex flex-col items-center gap-2 text-sand/60 hover:text-gold transition-colors duration-300"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase rotate-90 origin-center translate-y-[-8px]">
          Scroll
        </span>
        <ArrowDown size={16} className="animate-float-y mt-6" />
      </motion.a>
    </section>
  );
}
